import React from "react";
import { Box, Button, Stack } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { isCanonicalGastronomicCatalogNavigation } from "../../utils/siteExperienceDefaults";
import { navbarCustomButtonStyle, navbarLinkStyle } from "./navbarVisualOwnership";

function navButtonSx({ active, style = {} }) {
  return {
    position: "relative",
    minHeight: 42,
    px: { md: 1.05, lg: 1.55 },
    borderRadius: 999,
    color: active ? style.activeTextColor : style.textColor,
    bgcolor: active ? style.activeBackgroundColor : style.backgroundColor,
    border: "1px solid",
    borderColor: active ? style.activeBorderColor : style.borderColor,
    fontWeight: active ? 900 : 750,
    letterSpacing: "-.01em",
    minWidth: 0,
    maxWidth: { md: 154, lg: 202 },
    whiteSpace: "nowrap",
    "&:hover": {
      color: style.hoverTextColor,
      bgcolor: style.hoverBackgroundColor,
      borderColor: style.hoverBorderColor,
    },
    "& .MuiButton-endIcon": {
      color: active ? style.activeTextColor : (style.endIconColor || style.textColor),
    },
    "&::after": active
      ? {
          content: '\"\"',
          position: "absolute",
          left: 18,
          right: 18,
          bottom: 5,
          height: 2,
          borderRadius: 99,
          bgcolor: style.activeIndicatorColor,
        }
      : undefined,
  };
}

function NavLabel({ children }) {
  return (
    <Box
      component="span"
      title={typeof children === "string" ? children : undefined}
      sx={{
        display: "block",
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
}

export default function NavbarDesktopNavigation({
  location,
  productsEnabled,
  gastronomicMenuEnabled,
  sections,
  navigation,
  megaOpen,
  onHome,
  onProducts,
  onGastronomicMenu,
  onAbout,
  onHighlights,
  onContact,
  onCustomButton,
  onOpenMega,
  onOpenCustomMega,
  onCloseMegaSoon,
}) {
  const homeActive = location.pathname === "/";
  const productsActive = ["/producto", "/productos", "/servicio", "/servicios", "/catalogo"].some(
    (path) => location.pathname.startsWith(path),
  );
  const gastronomicMenuActive = location.pathname.startsWith("/carta");
  const contactActive = location.pathname.startsWith("/contact");
  const defaultGastronomicNavigation = isCanonicalGastronomicCatalogNavigation(navigation, gastronomicMenuEnabled);
  const homeLabel = String(navigation?.homeLabel ?? "").trim();
  const productsLabel = String(navigation?.productsLabel ?? "").trim();
  const aboutLabel = String(navigation?.aboutLabel ?? "").trim();
  const highlightsLabel = String(navigation?.highlightsLabel ?? "").trim();
  const contactLabel = String(navigation?.contactLabel ?? "").trim();
  const customButtons = Array.isArray(navigation?.buttons)
    ? navigation.buttons
        .map((item, sourceIndex) => ({ item, sourceIndex }))
        .filter(({ item }) => String(item?.label || "").trim() && (item?.behavior === "mega-menu" || String(item?.target || "").trim()))
        .slice(0, 8)
    : [];

  const styleFor = (key) => navbarLinkStyle(navigation, key, "desktop");

  return (
    <Stack direction="row" spacing={0.2} alignItems="center" sx={{ minWidth: 0 }}>
      {navigation?.showHomeLink !== false && homeLabel ? (
        <Button data-landing-field="navigation.homeLabel" onClick={onHome} sx={navButtonSx({ active: homeActive, style: styleFor("home") })}>
          <NavLabel>{homeLabel}</NavLabel>
        </Button>
      ) : null}

      {gastronomicMenuEnabled && navigation?.showProductsLink !== false && productsLabel ? (
        <Button
          data-landing-field="navigation.productsLabel"
          onClick={onGastronomicMenu}
          sx={navButtonSx({ active: gastronomicMenuActive, style: styleFor("catalog") })}
        >
          <NavLabel>Carta</NavLabel>
        </Button>
      ) : null}

      {productsEnabled && !defaultGastronomicNavigation && navigation?.showProductsLink !== false && productsLabel ? (
        <Button
          data-landing-field="navigation.productsLabel"
          onClick={onProducts}
          onMouseEnter={onOpenMega}
          onMouseLeave={onCloseMegaSoon}
          onFocus={onOpenMega}
          aria-haspopup="true"
          aria-expanded={megaOpen}
          endIcon={
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: 20,
                transform: megaOpen ? "rotate(180deg)" : "none",
                transition: "transform .2s ease",
              }}
            />
          }
          sx={navButtonSx({ active: productsActive || megaOpen, style: styleFor("catalog") })}
        >
          <NavLabel>{productsLabel}</NavLabel>
        </Button>
      ) : null}

      {sections?.showAbout !== false && navigation?.showAboutLink !== false && aboutLabel ? (
        <Button data-landing-field="navigation.aboutLabel" onClick={onAbout} sx={navButtonSx({ active: false, style: styleFor("about") })}>
          <NavLabel>{aboutLabel}</NavLabel>
        </Button>
      ) : null}

      {sections?.showHighlights !== false && navigation?.showHighlightsLink !== false && highlightsLabel ? (
        <Button data-landing-field="navigation.highlightsLabel" onClick={onHighlights} sx={navButtonSx({ active: false, style: styleFor("highlights") })}>
          <NavLabel>{highlightsLabel}</NavLabel>
        </Button>
      ) : null}

      {sections?.showContact !== false && navigation?.showContactLink !== false && contactLabel ? (
        <Button
          data-landing-field="navigation.contactLabel"
          onClick={onContact}
          sx={navButtonSx({ active: contactActive, style: styleFor("contact") })}
        >
          <NavLabel>{contactLabel}</NavLabel>
        </Button>
      ) : null}

      {customButtons.map(({ item, sourceIndex }) => {
        const isMegaMenu = item?.behavior === "mega-menu";
        const buttonStyle = navbarCustomButtonStyle(item, "desktop");
        return (
          <Button
            key={item.id}
            data-navbar-button-id={item.id}
            data-landing-field={`navigation.buttons.${sourceIndex}.label`}
            onClick={() => onCustomButton?.(item)}
            onMouseEnter={isMegaMenu ? () => onOpenCustomMega?.(item) : undefined}
            onMouseLeave={isMegaMenu ? onCloseMegaSoon : undefined}
            onFocus={isMegaMenu ? () => onOpenCustomMega?.(item) : undefined}
            aria-haspopup={isMegaMenu ? "true" : undefined}
            aria-expanded={isMegaMenu ? megaOpen : undefined}
            endIcon={isMegaMenu ? <KeyboardArrowDownRoundedIcon sx={{ fontSize: 20 }} /> : undefined}
            sx={navButtonSx({ active: isMegaMenu && megaOpen, style: buttonStyle })}
          >
            <NavLabel>{String(item.label).trim()}</NavLabel>
          </Button>
        );
      })}
    </Stack>
  );
}
